import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import YTPlayer from 'youtube-player';
import { parseYoutubeId, parseTimeToSeconds, formatSeconds } from './lib/youtube';

type ButtonData = {
  id: string;
  label: string;
  youtube_id: string;
  from_seconds: number;
  to_seconds: number;
  color: string | null;
  audio_key: string | null;
};

type FormState = {
  id: string | null;
  label: string;
  youtubeInput: string;
  from: string;
  to: string;
};

const emptyForm: FormState = { id: null, label: '', youtubeInput: '', from: '', to: '' };

function ManagePage() {
  const [data, setData] = useState<ButtonData[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<string | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const playerRef = useRef<ReturnType<typeof YTPlayer>>();

  const load = () => {
    fetch('/api/buttons')
      .then((r) => r.json())
      .then(setData);
  };

  useEffect(() => { load(); }, []);

  const sortedData = useMemo(
    () => [...data].sort((a, b) => sortOrder === 'asc'
      ? a.label.localeCompare(b.label, 'it')
      : b.label.localeCompare(a.label, 'it')),
    [data, sortOrder]
  );

  const resetForm = () => {
    setForm(emptyForm);
    setFormError(null);
    setStatus(null);
  };

  const edit = (button: ButtonData) => {
    setForm({
      id: button.id,
      label: button.label,
      youtubeInput: button.youtube_id,
      from: formatSeconds(button.from_seconds),
      to: formatSeconds(button.to_seconds),
    });
    setFormError(null);
    setStatus(null);
  };

  const remove = async (id: string) => {
    if (!window.confirm('Rimuovere questo bottone?')) return;
    await fetch(`/api/buttons/${id}`, { method: 'DELETE' });
    load();
    if (form.id === id) resetForm();
  };

  const getVideoDuration = async (youtubeId: string): Promise<number> => {
    if (playerRef.current) playerRef.current.destroy();
    const player = YTPlayer('yt-extract-player');
    playerRef.current = player;

    await player.loadVideoById(youtubeId);
    let duration = 0;
    for (let i = 0; i < 20 && !duration; i++) {
      duration = await player.getDuration();
      if (!duration) await new Promise((resolve) => setTimeout(resolve, 200));
    }

    player.destroy();
    playerRef.current = undefined;
    return duration;
  };

  const recordSegment = async (
    displayStream: MediaStream,
    youtubeId: string,
    from: number,
    to: number,
    label = youtubeId
  ): Promise<Blob> => {
    const audioTracks = displayStream.getAudioTracks();
    const recordStream = new MediaStream(audioTracks);
    const recorder = new MediaRecorder(recordStream, { mimeType: 'audio/webm;codecs=opus' });
    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

    // Live level meter on the captured track: if it never rises above silence
    // for the whole segment, the wrong tab/window was almost certainly shared.
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    audioContext.createMediaStreamSource(recordStream).connect(analyser);
    const levels = new Uint8Array(analyser.fftSize);
    let peak = 0;
    const meterInterval = setInterval(() => {
      analyser.getByteTimeDomainData(levels);
      for (let i = 0; i < levels.length; i++) {
        peak = Math.max(peak, Math.abs(levels[i] - 128));
      }
    }, 100);

    if (playerRef.current) playerRef.current.destroy();
    const player = YTPlayer('yt-extract-player');
    playerRef.current = player;

    await player.loadVideoById(youtubeId, from);
    await player.playVideo();

    recorder.start();
    await new Promise((resolve) => setTimeout(resolve, Math.max(0, (to - from) * 1000)));
    recorder.stop();
    await new Promise((resolve) => { recorder.onstop = resolve; });

    clearInterval(meterInterval);
    audioContext.close();
    player.destroy();
    playerRef.current = undefined;

    if (peak < 2) {
      throw new Error(
        `Il clip di "${label}" è risultato silenzioso: probabilmente è stata condivisa la scheda sbagliata. ` +
        'Riprova selezionando "Questa scheda"/"Soundy" nel prompt di condivisione.'
      );
    }

    return new Blob(chunks, { type: 'audio/webm' });
  };

  const uploadAudio = async (buttonId: string, blob: Blob) => {
    await fetch(`/api/buttons/${buttonId}/audio`, {
      method: 'PUT',
      headers: { 'Content-Type': 'audio/webm' },
      body: blob,
    });
  };

  const requestDisplayAudioStream = async (): Promise<MediaStream> => {
    // preferCurrentTab (Chrome-only, non-standard) auto-selects this tab in the
    // share picker: without it Chrome sometimes hides the current tab from the
    // list, so picking any other tab captures silence (the hidden YouTube
    // player only plays audio in this tab). Chrome-only is an accepted
    // limitation for now.
    const displayStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
      preferCurrentTab: true,
    } as DisplayMediaStreamOptions);
    if (displayStream.getAudioTracks().length === 0) {
      displayStream.getTracks().forEach((t) => t.stop());
      throw new Error('Nessuna traccia audio condivisa. Assicurati di selezionare "Condividi audio scheda" e di scegliere QUESTA scheda (Soundy), non un\'altra.');
    }
    return displayStream;
  };

  const extractAudio = async (buttonId: string, youtubeId: string, from: number, to: number) => {
    setStatus('In attesa del permesso di condivisione audio della scheda…');
    const displayStream = await requestDisplayAudioStream();
    try {
      setStatus('Estrazione in corso…');
      const blob = await recordSegment(displayStream, youtubeId, from, to, form.label.trim() || youtubeId);
      setStatus('Caricamento clip audio…');
      await uploadAudio(buttonId, blob);
    } finally {
      displayStream.getTracks().forEach((t) => t.stop());
    }
  };

  const extractAllMissing = async () => {
    const missing = data.filter((b) => !b.audio_key);
    if (missing.length === 0) return;

    setBulkError(null);
    setBulkRunning(true);
    setBulkStatus('In attesa del permesso di condivisione audio della scheda…');

    try {
      const displayStream = await requestDisplayAudioStream();
      try {
        for (let i = 0; i < missing.length; i++) {
          const button = missing[i];
          setBulkStatus(`(${i + 1}/${missing.length}) Estrazione "${button.label}"…`);
          const blob = await recordSegment(displayStream, button.youtube_id, button.from_seconds, button.to_seconds, button.label);
          setBulkStatus(`(${i + 1}/${missing.length}) Caricamento "${button.label}"…`);
          await uploadAudio(button.id, blob);
        }
        setBulkStatus(`Fatto: estratti ${missing.length} clip audio.`);
      } finally {
        displayStream.getTracks().forEach((t) => t.stop());
      }
      load();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Errore durante l\'estrazione massiva.');
    } finally {
      setBulkRunning(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const youtubeId = parseYoutubeId(form.youtubeInput);
    if (!youtubeId) {
      setFormError('Link o ID YouTube non valido.');
      return;
    }
    let from = 0;
    if (form.from.trim()) {
      const parsed = parseTimeToSeconds(form.from);
      if (parsed == null) {
        setFormError('Valore "da" non valido (formato mm:ss o secondi).');
        return;
      }
      from = parsed;
    }

    let to: number | null = null;
    if (form.to.trim()) {
      const parsed = parseTimeToSeconds(form.to);
      if (parsed == null) {
        setFormError('Valore "a" non valido (formato mm:ss o secondi).');
        return;
      }
      to = parsed;
    }

    if (to != null && to <= from) {
      setFormError('Intervallo from/to non valido: "a" deve essere maggiore di "da".');
      return;
    }
    if (!form.label.trim()) {
      setFormError('Il nome del bottone è obbligatorio.');
      return;
    }

    setBusy(true);
    try {
      if (to == null) {
        setStatus('Recupero durata video…');
        to = await getVideoDuration(youtubeId);
        if (!to || to <= from) {
          throw new Error('Impossibile determinare la durata del video.');
        }
      }

      const payload = { label: form.label.trim(), youtube_id: youtubeId, from_seconds: from, to_seconds: to };
      const res = await fetch(form.id ? `/api/buttons/${form.id}` : '/api/buttons', {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const saved: ButtonData = await res.json();

      if (!saved.audio_key) {
        await extractAudio(saved.id, youtubeId, from, to);
      }

      setStatus('Salvato.');
      load();
      resetForm();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Errore durante il salvataggio.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='min-h-screen w-full flex flex-col bg-[oklch(0.15_0.012_260)] text-[oklch(0.93_0.006_260)]'>
      <header className='px-4 sm:px-8 md:px-12 pt-7 pb-6 flex items-center justify-between flex-wrap gap-2 border-b border-[oklch(0.28_0.015_260)]'>
        <h1 className='font-bold text-2xl tracking-tight' style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Gestione suoni
        </h1>
        <Link
          to='/'
          className='text-[10px] sm:text-xs tracking-wide uppercase text-[oklch(0.55_0.01_260)] whitespace-nowrap no-underline hover:text-[oklch(0.7_0.01_260)] border border-[oklch(0.32_0.016_260)] rounded-md px-2 sm:px-2.5 py-1.5'
        >
          Torna alla soundboard
        </Link>
      </header>

      <main className='flex-1 px-4 sm:px-8 md:px-12 py-8 flex flex-col gap-10 max-w-3xl'>
        <form onSubmit={submit} className='flex flex-col gap-4 border border-[oklch(0.3_0.016_260)] rounded-2xl p-6'>
          <h2 className='font-semibold text-lg' style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {form.id ? 'Modifica bottone' : 'Nuovo bottone'}
          </h2>

          <label className='flex flex-col gap-1 text-sm'>
            Nome bottone
            <input
              className='bg-[oklch(0.2_0.014_260)] border border-[oklch(0.3_0.016_260)] rounded-[10px] py-2.5 px-3 outline-none focus:border-[oklch(0.7_0.15_70)]'
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder='Es. Applausi'
            />
          </label>

          <label className='flex flex-col gap-1 text-sm'>
            Link YouTube
            <input
              className='bg-[oklch(0.2_0.014_260)] border border-[oklch(0.3_0.016_260)] rounded-[10px] py-2.5 px-3 outline-none focus:border-[oklch(0.7_0.15_70)]'
              value={form.youtubeInput}
              onChange={(e) => setForm((f) => ({ ...f, youtubeInput: e.target.value }))}
              placeholder='https://www.youtube.com/watch?v=...'
            />
          </label>

          <div className='flex flex-wrap gap-4'>
            <label className='flex flex-col gap-1 text-sm flex-1 min-w-[140px]'>
              Da (mm:ss) — opzionale
              <input
                className='bg-[oklch(0.2_0.014_260)] border border-[oklch(0.3_0.016_260)] rounded-[10px] py-2.5 px-3 outline-none focus:border-[oklch(0.7_0.15_70)]'
                value={form.from}
                onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
                placeholder='inizio video'
              />
            </label>
            <label className='flex flex-col gap-1 text-sm flex-1 min-w-[140px]'>
              A (mm:ss) — opzionale
              <input
                className='bg-[oklch(0.2_0.014_260)] border border-[oklch(0.3_0.016_260)] rounded-[10px] py-2.5 px-3 outline-none focus:border-[oklch(0.7_0.15_70)]'
                value={form.to}
                onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
                placeholder='fine video'
              />
            </label>
          </div>

          <p className='text-xs text-[oklch(0.55_0.01_260)] -mt-1'>
            Se lasci vuoto "A", viene rilevata automaticamente la durata reale del video e usata come fine.
            I bottoni importati dal vecchio foglio Google senza un intervallo esplicito usano invece
            i primi 30 secondi a partire da "Da" (o dall'inizio, se anche "Da" è vuoto).
          </p>

          {formError && <p className='text-sm text-[oklch(0.7_0.15_30)]'>{formError}</p>}
          {status && <p className='text-sm text-[oklch(0.7_0.1_140)]'>{status}</p>}

          <div className='flex gap-3'>
            <button
              type='submit'
              disabled={busy}
              className={clsx(
                'rounded-[10px] py-2.5 px-4 font-semibold text-sm bg-[oklch(0.78_0.16_70)] text-[oklch(0.18_0.01_70)]',
                busy && 'opacity-50 cursor-not-allowed'
              )}
            >
              {busy ? 'Salvataggio…' : form.id ? 'Salva modifiche' : 'Crea bottone'}
            </button>
            {form.id && (
              <button
                type='button'
                onClick={resetForm}
                className='rounded-[10px] py-2.5 px-4 font-semibold text-sm border border-[oklch(0.3_0.016_260)]'
              >
                Annulla
              </button>
            )}
          </div>

          <p className='text-xs text-[oklch(0.55_0.01_260)]'>
            Al salvataggio verrà chiesto di condividere l'audio: il browser riproduce il video per estrarre
            solo il segmento selezionato, senza salvare l'intero video. Su Chrome dovrebbe pre-selezionare
            automaticamente questa scheda — se ti viene comunque chiesto di scegliere, seleziona sempre
            <strong> "Questa scheda"/"Soundy"</strong> (mai un'altra scheda), altrimenti l'audio estratto
            risulterà muto. Funzionalità testata solo su Chrome.
          </p>
        </form>

        <div className='flex flex-col gap-3'>
          <div className='flex items-center justify-between flex-wrap gap-2'>
            <div className='flex items-center gap-2'>
              <h2 className='font-semibold text-lg' style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Bottoni esistenti
              </h2>
              <button
                type='button'
                onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                title={sortOrder === 'asc' ? 'Ordine A→Z (clicca per Z→A)' : 'Ordine Z→A (clicca per A→Z)'}
                className='shrink-0 rounded-md py-1 px-2.5 text-xs border border-[oklch(0.32_0.016_260)] hover:border-[oklch(0.42_0.02_260)] cursor-pointer'
              >
                {sortOrder === 'asc' ? 'A→Z' : 'Z→A'}
              </button>
            </div>
            {data.some((b) => !b.audio_key) && (
              <button
                type='button'
                onClick={extractAllMissing}
                disabled={bulkRunning}
                className={clsx(
                  'rounded-[10px] py-2 px-3.5 font-semibold text-xs border border-[oklch(0.42_0.02_260)]',
                  bulkRunning && 'opacity-50 cursor-not-allowed'
                )}
              >
                {bulkRunning
                  ? 'Estrazione in corso…'
                  : `Estrai audio mancanti (${data.filter((b) => !b.audio_key).length})`}
              </button>
            )}
          </div>

          {bulkStatus && <p className='text-sm text-[oklch(0.7_0.1_140)]'>{bulkStatus}</p>}
          {bulkError && <p className='text-sm text-[oklch(0.7_0.15_30)]'>{bulkError}</p>}
          {data.some((b) => !b.audio_key) && (
            <p className='text-xs text-[oklch(0.55_0.01_260)]'>
              Ti verrà chiesto una sola volta di condividere l'audio: il browser riproduce in sequenza
              tutti i video mancanti ed estrae il segmento di ciascuno. Se ti viene chiesto di scegliere
              una scheda, seleziona sempre <strong>"Questa scheda"/"Soundy"</strong> (mai un'altra),
              altrimenti l'audio estratto risulterà muto. Tieni questa scheda attiva finché non termina.
            </p>
          )}

          {sortedData.map((button) => (
            <div
              key={button.id}
              className='flex items-center justify-between flex-wrap gap-2 border border-[oklch(0.3_0.016_260)] rounded-xl px-5 py-4'
            >
              <div className='min-w-0'>
                <div className='font-semibold break-words'>{button.label}</div>
                <div className='text-xs text-[oklch(0.55_0.01_260)] mt-1 break-words'>
                  {button.youtube_id} · {formatSeconds(button.from_seconds)}–{formatSeconds(button.to_seconds)}
                  {' · '}
                  {button.audio_key ? 'audio pronto' : 'audio mancante'}
                </div>
              </div>
              <div className='flex gap-2 shrink-0'>
                <button
                  onClick={() => edit(button)}
                  className='rounded-md py-1.5 px-3 text-xs border border-[oklch(0.32_0.016_260)]'
                >
                  Modifica
                </button>
                <button
                  onClick={() => remove(button.id)}
                  className='rounded-md py-1.5 px-3 text-xs border border-[oklch(0.4_0.03_30)] text-[oklch(0.75_0.13_30)]'
                >
                  Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      <div id='yt-extract-player' className='hidden'></div>
    </div>
  );
}

export default ManagePage;
